

import _ from "lodash";

import { syncTimerTemplates } from "../actions/inventoryActions";
import env from "../constants/env";
import browserHistory from "../history";
import { getJsonFileFromS3, printTemplate, putJsonFileToS3, saveHistory, sendEmail } from "../persistence/s3";
import state from "../state/state";
import { refreshCredentials } from "../utils/securityUtil";
import {
	getLs,
	normalizeActivity,
	setFullSessionName,
	recurseReplaceSpeeds,
	recurseConvertChecklistToMarkdown,
	getQueryStringParam,
} from "../utils/utils";

import { showError, showSuccess } from "./alertActions";
import { getDriveObjectsAsMap } from "./drive";
import { assembleActivitiesForMobile } from "./inventoryActions";
import { updateItunesSubscription, logout } from "./portalActions";
import { getIdentityId, getUsername, isOrganizationUser } from "./user";
import { getOrgInfo, getOrgId, updateMemberInfoInDb } from "./orgsActions";

import { navigateTo } from "./navActions";

import { publishActivityToSignedUrls } from "./publish";
import { getAssignmentScheduleForCurrentUser, getHealthStreamAssignmentScheduleForCurrentUser } from "./assignmentActions";

const authorSkuSelectionListeners = new Set();
const authorSkuFilterListeners = new Set();
const MOBILE_ORG_INFO_FIELDS = ["id", "name", "logoUrl", "preferences"];

function resolveSelectedPricingPayloadInvoiceItems(pricing = {}) {
	if (!pricing || typeof pricing !== "object") {
		return null;
	}

	const pricingContractProto = pricing.pricingContractProto;
	if (pricingContractProto && Array.isArray(pricingContractProto.documents) && pricingContractProto.documents.length > 0) {
		const activeDocument = pricingContractProto.documents.find((document) => document?.id === pricingContractProto.activeDocumentId)
			|| pricingContractProto.documents[pricingContractProto.documents.length - 1];
		if (activeDocument) {
			return Array.isArray(activeDocument.invoiceItems) ? activeDocument.invoiceItems : [];
		}
	}

	if (Array.isArray(pricing.estimates) && pricing.selectedValue) {
		const selectedEstimate = pricing.estimates.find((estimate) => estimate?.value === pricing.selectedValue);
		if (selectedEstimate) {
			return Array.isArray(selectedEstimate.invoiceItems) ? selectedEstimate.invoiceItems : [];
		}
	}

	return null;
}

function hasManagedPricingContractProto(pricingContractProto) {
	return Boolean(
		pricingContractProto
		&& Array.isArray(pricingContractProto.documents)
		&& pricingContractProto.documents.length > 0
	);
}

function emitMobilePerfSample(label, durationMs, meta = {}) {
	if (typeof window === "undefined" || !window.ReactNativeWebView || typeof window.ReactNativeWebView.postMessage !== "function") {
		return;
	}

	try {
		postMessage({
			method: "perfSample",
			sample: {
				source: "cloud-webview",
				label,
				durationMs,
				meta,
				timestamp: Date.now()
			}
		});
	} catch (err) {
		console.error("Unable to emit mobile perf sample", err);
	}
}

function startPerfTimer(label, meta = null) {
	const start = Date.now();
	if (meta) {
		console.log(`[perf] ${label}: start`, meta);
	} else {
		console.log(`[perf] ${label}: start`);
	}

	return (extraMeta = null) => {
		const durationMs = Date.now() - start;
		const mergedMeta = _.assign({}, meta || {}, extraMeta || {});
		if (meta || extraMeta) {
			console.log(`[perf] ${label}: ${durationMs}ms`, mergedMeta);
		} else {
			console.log(`[perf] ${label}: ${durationMs}ms`);
		}
		emitMobilePerfSample(label, durationMs, mergedMeta);
		return durationMs;
	};
}

export function registerAuthorSkuSelectionListener(handler) {
	if (typeof handler === "function") {
		authorSkuSelectionListeners.add(handler);
	}
}

export function unregisterAuthorSkuSelectionListener(handler) {
	if (handler && authorSkuSelectionListeners.has(handler)) {
		authorSkuSelectionListeners.delete(handler);
	}
}

export function registerAuthorSkuFilterListener(handler) {
	if (typeof handler === "function") {
		authorSkuFilterListeners.add(handler);
	}
}

export function unregisterAuthorSkuFilterListener(handler) {
	if (handler && authorSkuFilterListeners.has(handler)) {
		authorSkuFilterListeners.delete(handler);
	}
}

function normalizeMemberInfo(memberInfoRaw) {
	if (_.isPlainObject(memberInfoRaw)) {
		return _.cloneDeep(memberInfoRaw);
	}

	if (_.isString(memberInfoRaw) && _.trim(memberInfoRaw) !== "") {
		try {
			const parsed = JSON.parse(memberInfoRaw);
			return _.isPlainObject(parsed) ? parsed : {};
		} catch (err) {
			console.warn("Unable to parse memberInfo for mobile bridge", err);
		}
	}

	return {};
}

function getCurrentMemberInfo() {
	return normalizeMemberInfo(_.get(state.get(["selectedOrg", "member"]), "memberInfo"));
}

function getCurrentMemberMobileUiPrefs() {
	const memberInfo = getCurrentMemberInfo();
	return _.isPlainObject(memberInfo.mobileUiPrefs) ? _.cloneDeep(memberInfo.mobileUiPrefs) : {};
}

async function updateCurrentMemberMobileUiPrefs(mobileUiPrefs = {}) {
	const member = state.get(["selectedOrg", "member"]);
	if (!member || !member.id || !member.userSubId || !_.isPlainObject(mobileUiPrefs)) {
		return false;
	}

	const nextMobileUiPrefs = _.assign({}, getCurrentMemberMobileUiPrefs(), mobileUiPrefs);
	const nextMemberInfo = _.assign({}, getCurrentMemberInfo(), {
		mobileUiPrefs: nextMobileUiPrefs
	});
	const serializedMemberInfo = JSON.stringify(nextMemberInfo);

	await updateMemberInfoInDb(member.id, member.userSubId, nextMemberInfo);

	state.set(["selectedOrg", "member", "memberInfo"], serializedMemberInfo);
	if (state.get(["user", "member"])) {
		state.set(["user", "member", "memberInfo"], serializedMemberInfo);
	}

	return true;
}

export function initializePostMessageListeners() {
	// WKWebView
	window.receivedMessageFromRN = async (e) => {
		console.log("receivedMessage: " + JSON.stringify(e));

		let obj = e;
		if (Array.isArray(obj)) {
			console.log("Call from mobile device (receivedMessageFromRN)", obj);
			// Compile checklists to send back to mobile app 
			refreshCredentials().then(() => {
				// sendInventoryAndUserInfoMessage().then(() => {
				return saveHistoryInstances(obj, {}, true);
				// }).catch((err) => {
				// postMessage({ "error": "Get checklists failed: " + err.message });
				// });
			}).catch(err => {
				postMessage({ "error": "Operation failed, unable to refresh security credentials: " + err.message });
			});


		} else if (obj.hasOwnProperty("itunesSubscription")) {
			let sub = e;
			let itunesSubscription = {
				plan: sub.itunesSubscription,
				expiration: sub.subscriptionExpiration,
				transactionId: sub.subscriptionTransactionId
			};
			refreshCredentials().then(() => {
				updateItunesSubscription(itunesSubscription);
			}).catch(err => {
				postMessage({ "error": "Operation failed, unable to refresh security credentials: " + err.message });
			});
		} else if (obj.hasOwnProperty("deviceId")) {
			let device = e;
			state.set(["device"], device);
			} else if (obj.hasOwnProperty("timerTemplates")) {
				const endMobileTimerTemplateSync = startPerfTimer("cloud.mobile.timerTemplatesSync", {
					refreshChecklists: !!obj.refreshChecklists,
					clientTemplateCount: _.isArray(obj.timerTemplates) ? obj.timerTemplates.length : 0
				});
				const clientTimerTemplates = obj.timerTemplates;
				const endLoadServerTimerTemplates = startPerfTimer("cloud.mobile.timerTemplatesSync.loadServerTemplates");
				const serverTimerTemplates = await getJsonFileFromS3(env.s3.contentBucket, "timerTemplates.json");
				endLoadServerTimerTemplates({
					serverTemplateCount: _.isArray(serverTimerTemplates) ? serverTimerTemplates.length : 0
				});
				const timerTemplates = syncTimerTemplates(clientTimerTemplates, serverTimerTemplates);

				// Save to s3
				const endSaveTimerTemplates = startPerfTimer("cloud.mobile.timerTemplatesSync.saveServerTemplates", {
					mergedTemplateCount: _.isArray(timerTemplates) ? timerTemplates.length : 0
				});
				await putJsonFileToS3(env.s3.contentBucket, "timerTemplates.json", timerTemplates);
				endSaveTimerTemplates();
				if (obj.refreshChecklists) {
					const endRefreshCredentials = startPerfTimer("cloud.mobile.timerTemplatesSync.refreshCredentials");
					refreshCredentials().then(() => {
						endRefreshCredentials();
						sendInventoryAndUserInfoMessage().then(() => {
							endMobileTimerTemplateSync();
						}).catch((err) => {
							endMobileTimerTemplateSync({
								error: err && err.message ? err.message : `${err}`
							});
							postMessage({ "error": "Get checklists failed: " + err.message });
						});
					}).catch(err => {
						endMobileTimerTemplateSync({
							error: err && err.message ? err.message : `${err}`
						});
						postMessage({ "error": "Operation failed, unable to refresh security credentials: " + err.message });
					});
				} else {
					endMobileTimerTemplateSync();
				}
			} else if (obj.hasOwnProperty("type") && obj.type === "checklist") {
				refreshCredentials().then(() => {
					saveHistoryInstances([obj]);
			}).catch(err => {
				postMessage({ "error": "Operation failed, unable to refresh security credentials: " + err.message });
			});
		} else if (obj.hasOwnProperty("method")) {
			switch (obj.method) {
				case "sendEmail": {
					await sendEmail(obj.toAddresses, obj.subject, obj.body, obj.htmlBody, obj.ccAddresses, obj.senderEmail, obj.replyToAddresses, obj.charset);
					break;
				}
				case "setPlaylistId": {
					refreshCredentials().then(() => {
						sendInventoryAndUserInfoMessage(null, obj.id).then(() => {
						}).catch((err) => {
							postMessage({ "error": "Get checklists failed: " + err.message });
						});
					}).catch(err => {
						postMessage({ "error": "Operation failed, unable to refresh security credentials: " + err.message });
					});
					break;
				}
				case "navigateTo": {
					navigateTo(obj.id);
					break;
				}
				case "logout": {
					logout();
					break;
				}
				case "refreshAssignments": {
					getAssignmentScheduleForCurrentUser().then(assignments => {
						sendAssignmentsMessage(assignments);
					});
					break;
				}
				case "refreshHealthStreamAssignments": {
					if (getOrgId() === "75a99644-d769-11ec-8330-069463faa5ff") {
						getHealthStreamAssignmentScheduleForCurrentUser().then(assignments => {
							sendHealthStreamAssignmentsMessage(assignments);
						});
					}
					break;
				}
				case "updateMobileUiPrefs": {
					try {
						await updateCurrentMemberMobileUiPrefs(obj.mobileUiPrefs);
					} catch (err) {
						console.error("Unable to update mobile UI prefs", err);
					}
					break;
				}
				case "saveInCameraRoll": {
					if (obj.success) {
						showSuccess("Save Media Asset", "Successfully saved media asset in camera roll.");
					} else {
						showError("Save Media Asset", "Local save of media asset failed, reason:" + obj.err);
					}
					break;
				}
				case "printInstance": {
					console.log("!*!*!* printInstance");
					// Set previewChecklist
					let jsonChecklist = obj.data;

					let lastTimestamp = (jsonChecklist.history[0].timestamp).replace(" ", "T");
					let fileName = jsonChecklist.id + "/" + lastTimestamp + "_" + jsonChecklist.instanceId + ".json";

					jsonChecklist = await publishActivityToSignedUrls(jsonChecklist, fileName);

					// Should convert to make sure has at least one list and one section and set proper type
					jsonChecklist = normalizeActivity(jsonChecklist);
					jsonChecklist = setFullSessionName(jsonChecklist);

					recurseReplaceSpeeds(jsonChecklist.children);
					recurseConvertChecklistToMarkdown(jsonChecklist.children, false);

					printTemplate(obj.name, jsonChecklist, obj.templateId, obj.callbackJsonToken);
					break;
				}
				default: {
					console.log("Nothing to do");
				}
			}
		} else {

			let user = e;

			let savePassword = getLs(user.username + ";savePassword");

			if (savePassword === null) {
				if (user.hasOwnProperty("sharedDevice")) {
					savePassword = !user.sharedDevice;
					state.set(["user", "sharedDevice"], user.sharedDevice);
				} else {
					savePassword = true;
				}
			}

			state.set(["user", "username"], user.username);
			if (savePassword) {
				state.set(["user", "password"], user.password);
			}
			state.set(["user", "savePassword"], savePassword);

			let rememberMe = getLs(user.username + ";rememberMe");
			if (rememberMe === null) {
				if (user.hasOwnProperty("sharedDevice")) {
					rememberMe = !user.sharedDevice;
					state.set(["user", "sharedDevice"], user.sharedDevice);
				} else {
					rememberMe = true;
				}
			}

			state.set(["user", "rememberMe"], rememberMe);

			console.log("index.js: " + JSON.stringify(state.get(["user"])));

			const path = "/login";
			browserHistory.push(path);
		}
	};

	window.addEventListener("message", (e) => {
		try {
			let data = e.data;
			if (data && typeof data === "string") {
				try {
					data = JSON.parse(data);
				} catch (err) {
					data = null;
				}
			}
			const isAuthorSkuMessage = data && data.clientItemId === "authorSkuPicker";
			if (isAuthorSkuMessage && (data.method === "addSearchHit" || data.method === "removeSkuSelection" || data.method === "setSkuSelections" || data.method === "closeSkuSearchModal")) {
				authorSkuSelectionListeners.forEach((handler) => {
					try {
						handler(data);
					} catch (err) {
						// ignore individual handler errors
					}
				});
				return;
			}
			if (isAuthorSkuMessage && data.method === "setFilterExpression") {
				authorSkuFilterListeners.forEach((handler) => {
					try {
						handler(data);
					} catch (err) {
						// ignore individual handler errors
					}
				});
				return;
			}
				if (data && data.pricing && data.pricing.invoiceItems) {
					const incomingItems = data.pricing.invoiceItems || [];
					const existingItems = state.get(["appState", "showPricing", "invoiceItems"]) || [];
					const resolvedPricingItems = resolveSelectedPricingPayloadInvoiceItems(data.pricing);
					const allowClear = data.pricing.forceClear === true
						|| (
							Array.isArray(incomingItems)
							&& incomingItems.length === 0
							&& Array.isArray(resolvedPricingItems)
							&& resolvedPricingItems.length === 0
						);
					const skipInvoiceItems = !allowClear && Array.isArray(incomingItems) && incomingItems.length === 0 && Array.isArray(existingItems) && existingItems.length > 0;
					if (!skipInvoiceItems) {
						state.set(["appState", "showPricing", "invoiceItemsTemp"], incomingItems);
					}
				state.set(["appState", "showPricing", "estimatesTemp"], data.pricing.estimates);
				state.set(["appState", "showPricing", "jobDetailsTemp"], data.pricing.jobDetails);
				state.set(["appState", "showPricing", "selectedValueTemp"], data.pricing.selectedValue);
				if (Object.prototype.hasOwnProperty.call(data.pricing, "pricingSnapshotId")) {
					state.set(["appState", "showPricing", "pricingSnapshotIdTemp"], data.pricing.pricingSnapshotId || "base");
				}
				if (Object.prototype.hasOwnProperty.call(data.pricing, "pricingContractProto")) {
					const incomingPricingContractProto = data.pricing.pricingContractProto || null;
					const existingPricingContractProto = state.get(["appState", "showPricing", "pricingContractProtoTemp"])
						|| state.get(["appState", "showPricing", "pricingContractProto"])
						|| state.get(["appState", "showPricing", "checklist", "pricingContractProto"])
						|| null;
					const shouldPreserveExistingPricingContractProto = (
						hasManagedPricingContractProto(existingPricingContractProto)
						&& !hasManagedPricingContractProto(incomingPricingContractProto)
					);
					if (!shouldPreserveExistingPricingContractProto) {
						state.set(["appState", "showPricing", "pricingContractProtoTemp"], incomingPricingContractProto);
					}
				}
			}
		} catch (err) {
			// ignored
		}
	});

	// UIWebView
	document.addEventListener("message", async (e) => {
		console.log("!@#!@# DOCUMENT LISTENER");
		let obj = JSON.parse(e.data);
		if (Array.isArray(obj)) {
			console.log("Call from mobile device (receivedMessageFromRN)", obj);
			// Compile checklists to send back to mobile app 
			refreshCredentials().then(() => {
				// sendInventoryAndUserInfoMessage().then(() => {
				return saveHistoryInstances(obj, {}, true);
				// }).catch((err) => {
				// postMessage({ "error": "Get checklists failed: " + err.message });
				// });
			}).catch(err => {
				postMessage({ "error": "Operation failed, unable to refresh security credentials: " + err.message });
			});
		} else if (obj.hasOwnProperty("pricing")) {
			console.log("!@#!@# MESSAGE 2", JSON.stringify(obj));
		} else if (obj.hasOwnProperty("deviceId")) {
			let device = obj;
			state.set(["device"], device);
		} else if (obj.hasOwnProperty("timerTemplates")) {
			const clientTimerTemplates = obj.timerTemplates;
			const serverTimerTemplates = await getJsonFileFromS3(env.s3.contentBucket, "timerTemplates.json");
			const timerTemplates = syncTimerTemplates(clientTimerTemplates, serverTimerTemplates);

			// Save to s3
			await putJsonFileToS3(env.s3.contentBucket, "timerTemplates.json", timerTemplates);
			if (obj.refreshChecklists) {
				refreshCredentials().then(() => {
					sendInventoryAndUserInfoMessage().then(() => {
					}).catch((err) => {
						postMessage({ "error": "Get checklists failed: " + err.message });
					});
				}).catch(err => {
					postMessage({ "error": "Operation failed, unable to refresh security credentials: " + err.message });
				});
			}
		} else if (obj.hasOwnProperty("type") && obj.type === "checklist") {
			refreshCredentials().then(() => {
				saveHistoryInstances([obj]);
			}).catch(err => {
				postMessage({ "error": "Operation failed, unable to refresh security credentials: " + err.message });
			});
		} else if (obj.hasOwnProperty("method")) {
			switch (obj.method) {
				case "navigateTo": {
					navigateTo(obj.id);
					break;
				}
				case "logout": {
					logout();
					break;
				}
				case "refreshAssignments": {
					getAssignmentScheduleForCurrentUser().then(assignments => {
						sendAssignmentsMessage(assignments);
					});
					break;
				}
				case "refreshHealthStreamAssignments": {
					if (getOrgId() === "75a99644-d769-11ec-8330-069463faa5ff") {
						getHealthStreamAssignmentScheduleForCurrentUser().then(assignments => {
							sendHealthStreamAssignmentsMessage(assignments);
						});
					}
					break;
				}
				case "saveInCameraRoll": {
					if (obj.success) {
						showSuccess("Save Media Asset", "Successfully saved media asset in camera roll.");
					} else {
						showError("Save Media Asset", "Local save of media asset failed, reason:" + obj.err);
					}
					break;
				}
				case "printInstance": {
					// Set previewChecklist
					let jsonChecklist = obj.data;

					let lastTimestamp = (jsonChecklist.history[0].timestamp).replace(" ", "T");
					let fileName = jsonChecklist.id + "/" + lastTimestamp + "_" + jsonChecklist.instanceId + ".json";

					jsonChecklist = await publishActivityToSignedUrls(jsonChecklist, fileName);

					// Should convert to make sure has at least one list and one section and set proper type
					jsonChecklist = normalizeActivity(jsonChecklist);
					jsonChecklist = setFullSessionName(jsonChecklist);

					recurseReplaceSpeeds(jsonChecklist.children);
					recurseConvertChecklistToMarkdown(jsonChecklist.children, false);

					printTemplate(obj.name, jsonChecklist, obj.templateId, obj.callbackJsonToken);
					break;
				}
				default: {
					console.log("Nothing to do");
				}
			}
		} else {
			let user = obj;

			let savePassword = getLs(user.username + ";savePassword");

			if (savePassword === null) {
				if (user.hasOwnProperty("sharedDevice")) {
					savePassword = !user.sharedDevice;
					state.set(["user", "sharedDevice"], user.sharedDevice);
				} else {
					savePassword = true;
				}
			}

			state.set(["user", "username"], user.username);
			if (savePassword) {
				state.set(["user", "password"], user.password);
			}
			state.set(["user", "savePassword"], savePassword);

			let rememberMe = getLs(user.username + ";rememberMe");
			if (rememberMe === null) {
				if (user.hasOwnProperty("sharedDevice")) {
					rememberMe = !user.sharedDevice;
					state.set(["user", "sharedDevice"], user.sharedDevice);
				} else {
					rememberMe = true;
				}
			}

			state.set(["user", "rememberMe"], rememberMe);

			console.log("index.js: " + JSON.stringify(state.get(["user"])));

			const path = "/login";
			browserHistory.push(path);
		}
	});
}

export function sendOrganizationInfoMessage(orgInfos) {
	const info = normalizeOrgInfoForMobile(_.first(orgInfos));
	let message = {
		type: "org-info",
		info
	};

	postMessage(message);
}

export function sendAssignmentsMessage(assignments) {
	// newChecklists is what pass to mobile app. Aggregate users, content and checklists
	let message = {
		type: "assignemnts-message",
		assignments
	};

	postMessage(message);
}


export function sendHealthStreamAssignmentsMessage(assignments) {
	let message = {
		type: "assignments-healthstream-message",
		assignments
	};

	postMessage(message);
}

export function saveHistoryInstances(obj, overrides = {}, refreshChecklists = false) {
	// Array of instances to save to s3
	let instances = obj;
	let files = [];
	let errLog = [];

	//			awsS3.logToCloudWatch(instances);

	for (let i = 0; i < instances.length; i++) {
		//alert("Error " + i);
		try {
			let instance = instances[i];
			let lastTimestamp = (instance.history[0].timestamp).replace(" ", "T");
			let fileName = instance.id + "/" + lastTimestamp + "_" + instance.instanceId + ".json";

			if (overrides.displayName) {
				instance.displayName = overrides.displayName;
			} else {
				const member = state.get(["selectedOrg", "member"]);
				instance.displayName = member.displayName;
			}

			files.push({ fileName: fileName, fileContent: instance });
		} catch (e) {
			errLog.push(e);
		}
	}

	if (errLog.length > 0) {
		//alert(JSON.stringify(errLog,null,2));
	}

	return saveHistory(env.s3.contentBucket, files, overrides.keyPrefixOverride, refreshChecklists);
}

export function sendInventoryAndUserInfoMessage(inventory = null, playlistId) {
	return new Promise(async (resolve, reject) => {
		const endSendInventoryAndUserInfo = startPerfTimer("cloud.mobile.sendInventoryAndUserInfoMessage", {
			inventoryProvided: _.isArray(inventory),
			providedInventoryCount: _.isArray(inventory) ? inventory.length : 0,
			playlistId: playlistId || ""
		});
		// newChecklists is what pass to mobile app. Aggregate users, content and checklists
		let message = {
			user: getUserInfo(),
			timerTemplates: [],
			checklists: [],
		};

		// Get timerTemplates
		try {
			const endLoadTimerTemplates = startPerfTimer("cloud.mobile.sendInventoryAndUserInfoMessage.loadTimerTemplates");
			const serverTimerTemplates = await getJsonFileFromS3(env.s3.contentBucket, "timerTemplates.json");
			message.timerTemplates = serverTimerTemplates;
			endLoadTimerTemplates({
				timerTemplateCount: _.isArray(serverTimerTemplates) ? serverTimerTemplates.length : 0
			});
		} catch (err) {
			console.log(err.message);
		}

		const apiVersion = getQueryStringParam("app-api-version");
		const deferInventory = getQueryStringParam("deferInventory") === true || getQueryStringParam("deferInventory") === 1 || getQueryStringParam("deferInventory") === "1" || getQueryStringParam("deferInventory") === "true" || getQueryStringParam("deferInventory") === "yes" || getQueryStringParam("deferInventory") === "on";

		if (isOrganizationUser() && apiVersion >= 8) {
			const orgInfo = await getOrgInfoForMobilePayload();
			message.orgInfo = orgInfo;

			// Send any env props
			let mobileAppEnv = {
				algolia: env.algolia,
				deferInventory
			};
			message.env = mobileAppEnv;

			postMessage(message);
			endSendInventoryAndUserInfo({
				checklistCount: _.isArray(message.checklists) ? message.checklists.length : 0,
				deferredInventory: true
			});
			resolve();
		} else {
			const endAssembleActivitiesForMobile = startPerfTimer("cloud.mobile.sendInventoryAndUserInfoMessage.assembleActivitiesForMobile");
			assembleActivitiesForMobile(inventory, playlistId).then(async (mobileInventory) => {
				endAssembleActivitiesForMobile({
					mobileInventoryCount: _.isArray(mobileInventory) ? mobileInventory.length : 0
				});
				// Now retrieve the content map to allow resolving of external resources on the fly
				// *** Going to fetch directly from mobile app
				if (apiVersion < 6) {
					message.externalResources = getDriveObjectsAsMap();
				}

				console.log("!#!#!# MOBILE INVENTORY", mobileInventory);

				// Add filter for invisible checklists
				const resultMobileInventory = mobileInventory.filter(activity => {
					if (activity.dependency) {
						return true;
					} else {
						if (!activity.hasOwnProperty("visible")) {
							return true;
						} else {
							return activity.visible;
						}
					}
				});

				console.log("!#!#!# RESULT MOBILE INVENTORY", resultMobileInventory);

				// const resultMobileInventory = mobileInventory.filter(activity => activity.visible !== false);

				message.checklists = resultMobileInventory;
				const orgInfo = await getOrgInfoForMobilePayload();
				message.orgInfo = orgInfo;

				// Send any env props
				let mobileAppEnv = {
					algolia: env.algolia,
					deferInventory
				};
				message.env = mobileAppEnv;

				postMessage(message);
				endSendInventoryAndUserInfo({
					checklistCount: resultMobileInventory.length,
					totalInventoryCount: _.isArray(mobileInventory) ? mobileInventory.length : 0
				});
				resolve();

			}).catch(err => {
				endSendInventoryAndUserInfo({
					error: err && err.message ? err.message : `${err}`
				});
				reject(err);
			});
		}
	});
}

function normalizeOrgInfoForMobile(orgInfo) {
	if (!orgInfo || !_.isObject(orgInfo)) {
		return null;
	}

	return _.pick(orgInfo, MOBILE_ORG_INFO_FIELDS);
}

function getCachedOrgInfoForMobilePayload() {
	const selectedOrg = normalizeOrgInfoForMobile(state.get(["selectedOrg"]));
	if (selectedOrg && selectedOrg.id) {
		return selectedOrg;
	}

	return null;
}

async function getOrgInfoForMobilePayload() {
	const endGetOrgInfo = startPerfTimer("cloud.mobile.sendInventoryAndUserInfoMessage.getOrgInfo");
	const cachedOrgInfo = getCachedOrgInfoForMobilePayload();
	if (cachedOrgInfo) {
		endGetOrgInfo({
			source: "state",
			orgId: cachedOrgInfo.id
		});
		return cachedOrgInfo;
	}

	const orgInfo = normalizeOrgInfoForMobile(await getOrgInfo());
	endGetOrgInfo({
		source: "network",
		orgId: orgInfo && orgInfo.id ? orgInfo.id : ""
	});
	return orgInfo;
}

export function sendTokenUpdatedMessage(token) {
	state.set(["user", "jwt"], token.jwt);
	postMessage({ method: "tokenUpdated", token });
}

export function sendLogoutMessage() {
	postMessage({ method: "logout" });
}

export function sendHideModalEditorMessage() {
	postMessage({ method: "hideModalEditor" });
}

export function sendDummyMessage() {
	postMessage({ method: "dummy" });
}

export function sendSaveInCameraRollMessage(content, fileName) {
	postMessage({
		method: "saveInCameraRoll",
		content,
		fileName
	});
}

export function sendRestoreSession(restore) {
	postMessage({ method: "restoreSession", restore });
}

export function sendLoginStartedMessage() {

}

export function sendLoginFinishedMessage() {

}

export function sendSyncStartMessage() {

}

export function sendSyncFinishedMessage() {

}

let embedded = false;

export function setEmbeddedAppState(_embedded) {
	embedded = _embedded;
}


export function postMessage(message, embed = embedded) {
	let success = false;


	if (window.ReactNativeWebView) {
		try {
			// console.log("About to post message", message, embed ? "*" : window.location.href);
			// const target = embed ? parent : window; //eslint-disable-line
			// target.ReactNativeWebView.postMessage(JSON.stringify(message), embed ? "*" : window.location.href);
			window.ReactNativeWebView.postMessage(JSON.stringify(message));
			success = true;
		} catch (e) {
			console.error(e);
		}
	} else if (window.postMessage) {
		try {
			console.log("About to post message", message, embed ? "*" : window.location.href);
			const target = embed ? parent : window; //eslint-disable-line
			target.postMessage(JSON.stringify(message), embed ? "*" : window.location.href);
			success = true;
		} catch (e) {
			console.error(e);
		}
	} else if (window.webView && window.webView.postMessage) {
		try {
			window.webView.postMessage(JSON.stringify(message));
			success = true;
		} catch (e) {
			console.error(e);
		}
	}

	return success;
}


function getUserInfo() {
	const user = state.get(["user"]);
	return {
		username: getUsername(),
		scr: state.get(["user", "password"]),
		savePassword: getLs(state.get(["user", "username"]) + ";savePassword"),
		rememberMe: getLs(state.get(["user", "username"]) + ";rememberMe"),
		identityId: getIdentityId(),
		subscriptionPlan: user.subscriptionPlan,
		subscriptionExpiration: user.hasOwnProperty("subscriptionExpiration") ? user.subscriptionExpiration : 0,
		member: state.get(["selectedOrg", "member"])
	};
}
